import { Link, useLocation, useRoute } from "wouter";
import { RotateCcw, Timer } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useGame } from "@/lib/queries";
import { useElapsed, formatDuration } from "@/lib/duration";
import { GameRoundsReport } from "@/components/GameRoundsReport";
import { GameInviteCard } from "@/components/GameInviteCard";
import { seedNewGameForRematch } from "@/lib/newGameSticky";

export default function GameDetail() {
  const [, params] = useRoute("/dashboard/games/:id");
  const [, setLocation] = useLocation();
  const id = params?.id ?? null;
  const { data: game, isLoading } = useGame(id);
  const elapsed = useElapsed(
    game?.startedAt ?? null,
    game?.completedAt ?? null,
    game?.durationSeconds ?? null,
  );

  if (isLoading) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }
  if (!game) {
    return <div className="p-8 text-muted-foreground">Game not found.</div>;
  }

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-3xl mx-auto space-y-6">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-display uppercase tracking-tight">
            {game.format === "singles" ? "Singles" : "Doubles"} ·{" "}
            {new Date(game.playedOn).toLocaleDateString()}
          </h1>
          <p className="text-xs text-muted-foreground inline-flex items-center gap-2 flex-wrap">
            <span
              className={`font-display uppercase tracking-wider px-1.5 py-0.5 rounded ${
                game.isVirtual
                  ? "bg-primary/15 text-primary"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {game.isVirtual ? "Virtual" : "Live"}
            </span>
            <span>{game.status}</span>
            {(elapsed.live || game.status === "complete") && (
              <span
                className={`inline-flex items-center gap-1 tabular-nums ${
                  elapsed.live ? "text-primary" : "text-muted-foreground"
                }`}
              >
                <Timer size={11} />
                {formatDuration(elapsed.seconds)}
              </span>
            )}
          </p>
        </div>
        {(game.status === "live" || game.status === "scheduled") && (
          <Button asChild>
            <Link
              href={
                game.isVirtual
                  ? `/dashboard/virtual/play/${game.shareCode}`
                  : `/score/${game.shareCode}`
              }
            >
              {game.status === "scheduled" ? "Start" : "Resume"}{" "}
              {game.isVirtual ? "game" : "scoring"}
            </Link>
          </Button>
        )}
        {/* Play Again: completed-game shortcut to a rematch with the
            same teams, format, target score. Seeds the New Game
            sticky-state keys with THIS game's config then routes to
            /dashboard/games/new — overrides any unrelated sticky
            values from a different matchup played in between. */}
        {game.status === "complete" && (
          <Button
            onClick={() => {
              seedNewGameForRematch({
                format: game.format,
                team1Players: game.team1Players,
                team2Players: game.team2Players,
                targetScore: game.targetScore,
              });
              setLocation("/dashboard/games/new");
            }}
          >
            <RotateCcw size={14} /> Play again
          </Button>
        )}
      </header>

      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
            <div
              className={`text-right text-lg ${
                game.winningTeam === 1 ? "font-bold" : "text-muted-foreground"
              }`}
            >
              {game.team1Players.map((p) => p.name).join(" & ")}
            </div>
            <div className="font-display text-5xl sm:text-6xl tabular-nums px-4 py-2 rounded-md border border-border bg-background">
              {game.team1Score}–{game.team2Score}
            </div>
            <div
              className={`text-left text-lg ${
                game.winningTeam === 2 ? "font-bold" : "text-muted-foreground"
              }`}
            >
              {game.team2Players.map((p) => p.name).join(" & ")}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Live-or-recent games: invite affordance with QR + native
          share. Hidden on completed games more than a day old —
          spectators don't need to "join" a finished game; the
          GameRoundsReport below covers post-game review. */}
      {game.status === "live" || game.status === "scheduled" ? (
        <GameInviteCard
          shareCode={game.shareCode}
          title="Share this game"
          subtitle={
            game.status === "live"
              ? "Live now — anyone with the link can watch."
              : "Game is set up — invite the opponent or a spectator."
          }
        />
      ) : null}

      {/* Round-by-round analytics replaces the old flat frame list.
          Renders cumulative score progression, per-round momentum,
          and (for pro-mode frames with bag detail) per-team accuracy
          breakdown. Skipped entirely when no frames are recorded yet
          to keep an in-progress quick game's detail page clean. */}
      {game.frames && game.frames.length > 0 ? (
        <Card>
          <CardContent className="p-4 sm:p-6">
            <GameRoundsReport game={game} />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
